import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import DataTable from '../../../ui/DataTable/DataTable';
import { ICONS } from '../../../ui/icon-map';
import { CellText, CellNumber, CellStatus, CellMuted, toneClass } from '../../../ui/DataTable/cells';
import DrillDownModal from '../components/DrillDownModal';
import { employeeStats, BRANCH_SHORT } from '../kpi.logic';

// Every employee who opened a contract in the period appears by themselves (the name from Ejar, shown
// with the display name from Settings > Staff Mappings). No targets: each one is read against the team
// average for the same period and against their own previous period.
const short = (b) => BRANCH_SHORT[b] || b;
const splitText = (pairs, fmt = (k) => k) => pairs.map(([k, n]) => `${fmt(k)} ${n}`).join(' · ');

const EmployeeKPIs = ({ allOps = [], filters, excluded = [] }) => {
    const { hasPermission } = usePermissions();
    const canDrillDown = hasPermission(PERMISSIONS.view_kpi_drilldown);
    const canExport = hasPermission('export_kpi');         // every KPI export: its own permission (phase 4)
    const [modal, setModal] = useState({ open: false, title: '', data: [] });

    const stats = useMemo(
        () => employeeStats(allOps, { from: filters.startDate, to: filters.endDate, branch: filters.branch }, excluded),
        [allOps, filters.startDate, filters.endDate, filters.branch, excluded],
    );

    const drill = (label, value, list, title, tone) => (canDrillDown && value > 0 ? (
        <button
            type="button"
            onClick={() => setModal({ open: true, title, data: list })}
            aria-label={label}
            className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-sm"
        >
            <CellNumber value={value} tone={tone} />
        </button>
    ) : (
        <CellNumber value={value} tone={tone} />
    ));

    const columns = [
        {
            key: 'name',
            header: 'Employee',
            cell: (e) => (
                <span className="inline-flex flex-col items-center">
                    <CellText>{e.name}</CellText>
                    <CellMuted>{e.isBranch ? 'Not counted as an employee' : splitText(e.branches, short)}</CellMuted>
                </span>
            ),
            exportValue: (e) => e.name,
        },
        {
            key: 'branch',
            header: 'Worked in',
            accessor: (e) => short(e.mainBranch),
            exportValue: (e) => splitText(e.branches, short),
            cell: (e) => <CellText>{short(e.mainBranch)}</CellText>,
        },
        {
            key: 'opened',
            header: 'Contracts',
            cell: (e) => drill(`${e.name}: contracts opened`, e.opened, e.list, `${e.name} - contracts opened`),
        },
        {
            key: 'openNow',
            header: 'Still open',
            cell: (e) => drill(`${e.name}: still open`, e.openNow, e.list.filter((op) => op.operationStatus === 'In-Progress'), `${e.name} - still open`, 'warning'),
        },
        {
            key: 'closed',
            header: 'Closed',
            cell: (e) => drill(`${e.name}: closed`, e.closed, e.list.filter((op) => op.operationStatus === 'Closed'), `${e.name} - closed`, 'success'),
        },
        {
            key: 'days',
            header: 'Working days',
            cell: (e) => <CellNumber value={e.days} />,
        },
        {
            key: 'perDay',
            header: 'Per working day',
            cell: (e) => <CellNumber value={e.perDay} />,
        },
        {
            key: 'types',
            header: 'Contract mix',
            sortable: false,
            accessor: (e) => splitText(e.types),
            cell: (e) => <CellMuted>{splitText(e.types)}</CellMuted>,
        },
        {
            key: 'share',
            header: 'Share of team',
            exportValue: (e) => e.share,
            cell: (e) => (
                <span className="inline-flex items-center justify-center gap-2">
                    <span className="h-1.5 w-20 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-2)' }}>
                        <span className="block h-full rounded-full" style={{ width: `${Math.min(e.share, 100)}%`, backgroundColor: 'var(--accent)' }} />
                    </span>
                    <span className="ds-cell-num min-w-[35px]">{e.share}%</span>
                </span>
            ),
        },
        {
            key: 'change',
            header: 'vs previous period',
            sortValue: (e) => (e.change == null ? -Infinity : e.change),
            exportValue: (e) => `${e.prevOpened} -> ${e.opened}`,
            cell: (e) => (
                <span className="inline-flex flex-col items-center">
                    <span className={`ds-cell-num ${toneClass(e.change == null ? 'neutral' : e.change >= 0 ? 'success' : 'danger')}`}>
                        {e.change == null ? 'New' : `${e.change > 0 ? '+' : ''}${e.change}%`}
                    </span>
                    <CellMuted>{`was ${e.prevOpened}`}</CellMuted>
                </span>
            ),
        },
        {
            key: 'standing',
            header: 'vs team average',
            sortValue: (e) => e.index,
            exportValue: (e) => e.standing.label,
            cell: (e) => <CellStatus status={e.standing.label} label={e.standing.label} tone={e.standing.tone} />,
        },
    ];

    return (
        <div className="flex flex-col gap-6">
            <DataTable
                title="Employee Performance"
                description={stats.people
                    ? `${stats.people} employees, ${stats.average} contracts each on average (${stats.total} in all). Everyone who opens a contract in Ejar appears here by themselves; names set in Settings as not counted go to their branch row.`
                    : 'Everyone who opens a contract in Ejar appears here by themselves.'}
                columns={columns}
                data={stats.rows}
                rowKey="name"
                pagination={false}
                searchPlaceholder="Search employees…"
                emptyIcon={ICONS.staff}
                emptyTitle="No contracts were opened in this period."
                exportFileName={canExport ? 'employee-performance' : undefined}
            />

            <DrillDownModal
                isOpen={modal.open}
                onClose={() => setModal({ ...modal, open: false })}
                title={modal.title}
                data={modal.data}
            />
        </div>
    );
};

export default EmployeeKPIs;
